import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { SearchBar } from '@/components/SearchBar';
import { MissingPersonCard } from '@/components/MissingPersonCard';
import { SEO } from '@/components/SEO';
import { getCases, getStats, subscribeToAlerts, aiSearch, getRecentCases } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { 
  MapPin, AlertTriangle, Search, ArrowRight, 
  Bell, Shield, Sparkles, Clock, Star, ChevronLeft, ChevronRight, Share2, MessageCircle, HelpCircle,
  Briefcase, X, FileSearch, Dna, Brain
} from 'lucide-react';
import ImpactDashboard from '@/components/ImpactDashboard';
import { SignupCTA } from '@/components/SignupCTA';

export default function HomePage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [recentCases, setRecentCases] = useState([]);
  const [urgentCases, setUrgentCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [location, setLocation] = useState('');
  const [showWorkspaceBanner, setShowWorkspaceBanner] = useState(true);
  
  // Check if user is an org user
  const isOrgUser = isAuthenticated && user?.organization_id;

  // Recent Carousel state
  const recentCarouselRef = useRef(null);
  const [canScrollRecentLeft, setCanScrollRecentLeft] = useState(false);
  const [canScrollRecentRight, setCanScrollRecentRight] = useState(true);

  const checkRecentScrollButtons = () => {
    if (recentCarouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = recentCarouselRef.current;
      setCanScrollRecentLeft(scrollLeft > 0);
      setCanScrollRecentRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollRecentCarousel = (direction) => {
    if (recentCarouselRef.current) {
      const scrollAmount = 400;
      recentCarouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkRecentScrollButtons, 300);
    }
  };

  // Get proxied image URL for NamUs images (to bypass CORS)
  const getProxiedImageUrl = (url) => {
    if (!url) return null;
    const API = process.env.REACT_APP_BACKEND_URL;
    if (url.includes('namus.gov') && url.includes('/Images/')) {
      const match = url.match(/CaseSets\/NamUs\/(MissingPersons|UnclaimedPersons|UnidentifiedPersons)\/Cases\/(\d+)\/Images\/(\d+)/);
      if (match) {
        const [, caseType, caseId, imageId] = match;
        return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail&case_type=${caseType}`;
      }
      const fallbackMatch = url.match(/Cases\/(\d+)\/Images\/(\d+)/);
      if (fallbackMatch) {
        const [, caseId, imageId] = fallbackMatch;
        return `${API}/api/external/namus-image/${caseId}/${imageId}?size=Thumbnail`;
      }
    }
    return url;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recentData, statsData] = await Promise.all([
        getRecentCases(8, false, 'missing,unidentified,unclaimed'),
        getStats()
      ]);
      
      setRecentCases(recentData.cases || []);
      
      const urgent = (recentData.cases || []).filter(c => c.is_urgent).slice(0, 4);
      setUrgentCases(urgent);
      
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      try {
        const [casesData, statsData] = await Promise.all([
          getCases({ limit: 8, status: 'active' }),
          getStats()
        ]);
        setRecentCases(casesData);
        setStats(statsData);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  // Regular search handler - navigates to browse page with search query
  const handleSearch = (query) => {
    if (query.trim()) {
      window.location.href = `/browse?search=${encodeURIComponent(query)}`;
    }
  };

  const handleAISearch = async (query) => {
    setSearchLoading(true);
    try {
      const result = await aiSearch(query);
      if (result.results?.length > 0) {
        toast.success(`Found ${result.results.length} matching cases`);
        window.location.href = `/browse?q=${encodeURIComponent(query)}`;
      } else {
        toast.info(result.ai_interpretation || 'No matching cases found');
      }
    } catch (error) {
      toast.error('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !location) {
      toast.error('Please enter both email and location');
      return;
    }
    try {
      await subscribeToAlerts({ email, location });
      toast.success('Successfully subscribed to alerts!');
      setEmail('');
      setLocation('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  return (
    <>
      <SEO
        title="Every Missing Person Deserves to Be Found"
        description="Search 100,000+ missing persons and unidentified remains cases from NamUs, NCMEC, FBI, Doe Network & Charley Project. AI-powered matching helps connect cases. Submit anonymous tips and help bring families together."
        url="/"
        keywords="missing persons search, NamUs, NCMEC, FBI missing persons, Doe Network, unidentified remains, John Doe, Jane Doe, find missing people, anonymous tips"
      />
      <div className="min-h-screen">
        {/* Org User Workspace Banner */}
        {isOrgUser && showWorkspaceBanner && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 relative">
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <Briefcase className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-medium truncate">
                  Welcome back! Your workspace has pending items to review.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => navigate('/org/dashboard')}
                  className="bg-white/20 hover:bg-white/30 text-white border-0 min-h-[36px]"
                  data-testid="workspace-banner-btn"
                >
                  Go to Workspace
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                <button
                  onClick={() => setShowWorkspaceBanner(false)}
                  className="p-1 hover:bg-white/20 rounded"
                  aria-label="Dismiss banner"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Hero Section - Compressed on mobile */}
        <section className="relative min-h-[75vh] md:min-h-[90vh] flex flex-col justify-center items-center text-center px-4 sm:px-6 py-4 md:py-0 overflow-hidden">
          {/* Background pattern - dark blue */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900 to-slate-950" />
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-10 w-72 h-72 bg-safety-orange-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-trust-blue-500/20 rounded-full blur-3xl" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative z-10 max-w-4xl mx-auto"
          >
          <Badge className="mb-3 md:mb-6 bg-safety-orange-500/20 text-safety-orange-400 border-safety-orange-500/30 px-3 md:px-4 py-1 md:py-1.5 text-xs md:text-sm font-medium">
            Find • Connect • Reunite
          </Badge>

          <h1 className="font-heading text-3xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight mb-3 md:mb-6 leading-[1.1] md:leading-tight text-white">
            Every <span className="text-safety-orange-500">Missing</span> Person{' '}
            <br className="hidden sm:block" />
            Deserves to Be <span className="text-trust-blue-400">Found</span>
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-slate-300 max-w-2xl mx-auto mb-3 md:mb-4 leading-snug md:leading-relaxed px-2">
            Connecting missing persons and unidentified remains using multi-factor similarity analysis, interactive mapping, and community-powered tips.
          </p>
          
          {/* Functional clarity sentence - what the platform actually does */}
          <p className="text-xs md:text-sm text-slate-400 max-w-xl mx-auto mb-4 md:mb-6 px-2">
            A searchable platform analyzing 119,000+ cases to surface potential connections using demographic, physical, and timeline similarity.
          </p>
          
          {/* Why we built this - Trust builder link */}
          <Link 
            to="/why" 
            className="inline-flex items-center gap-1.5 text-sm text-trust-blue-400 hover:text-trust-blue-300 transition-colors mb-4 md:mb-8"
            data-testid="hero-why-link"
          >
            <HelpCircle className="w-4 h-4" />
            Why we built this — and how we approach it responsibly
            <ArrowRight className="w-3 h-3" />
          </Link>
          
          {/* Human-centered line - mobile only */}
          <p className="md:hidden text-xs text-slate-400/80 italic mb-5">
            Every case represents a real person.
          </p>

          <SearchBar 
            onSearch={handleSearch}
            onAISearch={handleAISearch} 
            isLoading={searchLoading}
            placeholder="Search by name, location, or describe who you're looking for..."
          />

          {/* Mobile: Single secondary CTA (Browse), Report moved below */}
          {/* Desktop: Both CTAs side by side - Browse primary, Report secondary */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-3 md:gap-4 mt-6 md:mt-10">
            {/* Start Exploring - PRIMARY CTA */}
            <Link to="/browse" className="w-full sm:w-auto">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-safety-orange-500 hover:bg-safety-orange-600 text-white rounded-full px-8 md:px-10 py-5 md:py-6 font-bold shadow-lg hover:shadow-safety-orange-500/25 transition-all text-sm md:text-base"
                data-testid="hero-browse-btn"
              >
                <Search className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                Start Exploring Cases
              </Button>
            </Link>
            
            {/* Submit Case - SECONDARY CTA - Hidden on mobile, shown on desktop */}
            <Link to="/report" className="hidden sm:block">
              <Button 
                size="lg" 
                variant="outline"
                className="rounded-full px-8 py-6 font-semibold border-2 border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur"
                data-testid="hero-report-btn"
              >
                <AlertTriangle className="w-5 h-5 mr-2" />
                Submit Missing Person Case
              </Button>
            </Link>
          </div>
          
          {/* Mobile: Subtle Submit link below main CTAs */}
          <div className="sm:hidden mt-4 text-center">
            <Link 
              to="/report" 
              className="text-sm text-slate-400 hover:text-safety-orange-400 transition-colors inline-flex items-center gap-1.5"
            >
              <AlertTriangle className="w-3.5 h-3.5" />
              Need to submit a missing person case?
            </Link>
          </div>

          {/* Conversion CTA — only for logged-out visitors. Reddit/social
              traffic lands here with no offer; this surfaces the trial. */}
          {!isAuthenticated && (
            <div className="mt-8 md:mt-10">
              <h2 className="text-center text-xl sm:text-2xl font-bold text-white mb-2" data-testid="homepage-hero-cta-headline">
                Join the investigation
              </h2>
              <SignupCTA variant="hero" context="homepage-hero" ctaTestId="homepage-hero-signup-cta" />
            </div>
          )}
        </motion.div>

        {/* Platform Scope Stats - Simplified for mobile */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="relative z-10 mt-10 md:mt-16 max-w-4xl w-full px-4"
          >
            {/* Mobile: Vertical cards with one stat per row */}
            <div className="md:hidden space-y-3">
              <div className="bg-white/10 backdrop-blur rounded-xl border border-white/20 p-4 flex items-center gap-4">
                <div className="text-3xl font-bold text-safety-orange-400">{stats.active_cases?.toLocaleString()}+</div>
                <div className="text-sm text-slate-300 leading-tight">cases indexed • continuously updated</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl border border-white/20 p-4 flex items-center gap-4">
                <div className="text-3xl font-bold text-green-400">7</div>
                <div className="text-sm text-slate-300 leading-tight">national databases: NamUs, NCMEC, FBI, Doe Network, Charley Project, RNPED & more</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl border border-white/20 p-4 flex items-center gap-4">
                <div className="text-3xl font-bold text-purple-400">50</div>
                <div className="text-sm text-slate-300 leading-tight">states with active case coverage</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl border border-white/20 p-4 flex items-center gap-4">
                <div className="text-3xl font-bold text-trust-blue-400">100%</div>
                <div className="text-sm text-slate-300 leading-tight">anonymous tip submissions • privacy protected</div>
              </div>
              
              {/* Human-centered line - mobile only */}
              <p className="text-xs text-slate-400/70 italic text-center mt-4 px-2">
                One share could bring someone home.
              </p>
            </div>
            
            {/* Desktop: Original grid layout */}
            <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
              <Card className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-safety-orange-400 mb-1">{stats.active_cases?.toLocaleString()}+</div>
                  <div className="text-sm text-slate-300">Cases Indexed</div>
                  <div className="text-xs text-slate-400 mt-1">Continuously updated</div>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-green-400 mb-1">7</div>
                  <div className="text-sm text-slate-300">National Databases</div>
                  <div className="text-xs text-slate-400 mt-1">NamUs • NCMEC • FBI • Doe Network & more</div>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-purple-400 mb-1">50</div>
                  <div className="text-sm text-slate-300">States Covered</div>
                  <div className="text-xs text-slate-400 mt-1">Nationwide coverage</div>
                </CardContent>
              </Card>
              <Card className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl lg:text-4xl font-bold text-trust-blue-400 mb-1">100%</div>
                  <div className="text-sm text-slate-300">Anonymous Tips</div>
                  <div className="text-xs text-slate-400 mt-1">Privacy protected</div>
                </CardContent>
              </Card>
            </div>
            
            {/* Credibility anchor */}
            <p className="text-center text-xs text-slate-400/70 mt-6 italic">
              Built to support responsible investigative research and public awareness.
            </p>
          </motion.div>
        )}
      </section>

      {/* How It Works */}
      <section className="py-10 md:py-16 lg:py-24 bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-14">
            <Badge className="mb-3 md:mb-4 bg-trust-blue-100 text-trust-blue-700 dark:bg-trust-blue-900/30 dark:text-trust-blue-400">
              How It Works
            </Badge>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-slate-900 dark:text-white">
              Powered by Multi-Factor Analysis
            </h2>
            <p className="text-slate-600 dark:text-slate-300 max-w-2xl mx-auto text-sm md:text-base mb-2">
              Our system analyzes facial similarity, geographic proximity, and timeline alignment to surface cases that may warrant deeper review.
            </p>
            <p className="text-slate-700 dark:text-slate-200 font-medium text-sm mb-2">
              Designed to support responsible investigative review.
            </p>
            <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto text-xs">
              Used by researchers, journalists, and advocates analyzing active cases.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 lg:gap-8">
            <Link to="/report">
              <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 p-4 md:p-6 text-center hover:shadow-lg hover:scale-105 transition-all cursor-pointer">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-safety-orange-100 dark:bg-safety-orange-900/30 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <AlertTriangle className="w-5 h-5 md:w-7 md:h-7 text-safety-orange-500" />
                </div>
                <h3 className="font-heading font-semibold text-sm md:text-lg mb-1 md:mb-2 text-slate-900 dark:text-white">Report Cases</h3>
                <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 hidden md:block">Submit detailed missing person reports with photos and last known location.</p>
              </Card>
            </Link>

            <Link to="/map">
              <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 p-4 md:p-6 text-center hover:shadow-lg hover:scale-105 transition-all cursor-pointer">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-trust-blue-100 dark:bg-trust-blue-900/30 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <MapPin className="w-5 h-5 md:w-7 md:h-7 text-trust-blue-500" />
                </div>
                <h3 className="font-heading font-semibold text-sm md:text-lg mb-1 md:mb-2 text-slate-900 dark:text-white">Interactive Map</h3>
                <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 hidden md:block">View cases on an interactive map showing last seen locations across the country.</p>
              </Card>
            </Link>

            <Link to="/browse">
              <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 p-4 md:p-6 text-center hover:shadow-lg hover:scale-105 transition-all cursor-pointer">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-purple-100 dark:bg-purple-900/30 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <Sparkles className="w-5 h-5 md:w-7 md:h-7 text-purple-500" />
                </div>
                <h3 className="font-heading font-semibold text-sm md:text-lg mb-1 md:mb-2 text-slate-900 dark:text-white">AI-Powered Search</h3>
                <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 hidden md:block">Use natural language to search cases and find similar profiles with AI assistance.</p>
              </Card>
            </Link>

            <Link to="/tips">
              <Card className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 p-4 md:p-6 text-center hover:shadow-lg hover:scale-105 transition-all cursor-pointer">
                <div className="w-10 h-10 md:w-14 md:h-14 bg-green-100 dark:bg-green-900/30 rounded-xl md:rounded-2xl flex items-center justify-center mx-auto mb-2 md:mb-4">
                  <Shield className="w-5 h-5 md:w-7 md:h-7 text-green-500" />
                </div>
                <h3 className="font-heading font-semibold text-sm md:text-lg mb-1 md:mb-2 text-slate-900 dark:text-white">Anonymous Tips</h3>
                <p className="text-xs md:text-sm text-slate-600 dark:text-slate-400 hidden md:block">Submit tips anonymously to help ongoing investigations without revealing your identity.</p>
              </Card>
            </Link>
          </div>
          
          {/* AI Disclaimer */}
          <p className="text-center text-xs text-slate-400 dark:text-slate-500 mt-4 md:mt-6 italic">
            AI suggestions are investigative leads — not confirmations.
          </p>
        </div>
      </section>

      {/* Impact Dashboard */}
      <ImpactDashboard />



      {/* Urgent Cases */}
      {urgentCases.length > 0 && (
        <section className="py-10 md:py-16 lg:py-24 bg-red-50 dark:bg-red-950/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6 md:mb-10">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl lg:text-3xl font-bold">Urgent Cases</h2>
                  <p className="text-muted-foreground">These cases need immediate attention</p>
                </div>
              </div>
              <Link to="/browse?urgent=true">
                <Button variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full">
                  View All <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {urgentCases.map((person, index) => (
                <MissingPersonCard key={person.id} person={person} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recent Cases - Carousel */}
      <section className="py-10 md:py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950/50 dark:to-slate-900/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6 md:mb-10">
            <div className="flex items-center gap-3">
              <div className="p-2 md:p-3 bg-trust-blue-100 dark:bg-trust-blue-900/30 rounded-xl">
                <Clock className="w-5 h-5 md:w-6 md:h-6 text-trust-blue-500" />
              </div>
              <div>
                <h2 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold">Recent Cases</h2>
                <p className="text-xs md:text-sm text-muted-foreground">Newly added or recently updated</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Carousel Navigation */}
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollRecentCarousel('left')}
                disabled={!canScrollRecentLeft}
                className="rounded-full border-trust-blue-300 hover:bg-trust-blue-100 disabled:opacity-50 h-8 w-8 md:h-10 md:w-10"
              >
                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => scrollRecentCarousel('right')}
                disabled={!canScrollRecentRight}
                className="rounded-full border-trust-blue-300 hover:bg-trust-blue-100 disabled:opacity-50 h-8 w-8 md:h-10 md:w-10"
              >
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
              <Link to="/browse">
                <Button variant="ghost" className="text-trust-blue-500 hover:text-trust-blue-600 hover:bg-trust-blue-100 dark:hover:bg-trust-blue-900/30 rounded-full hidden sm:flex">
                  Browse All <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex gap-4 md:gap-6 overflow-hidden">
              {[...Array(4)].map((_, i) => (
                <Card key={i} className="animate-pulse flex-shrink-0 w-[280px] md:w-[320px]">
                  <div className="aspect-[4/5] bg-slate-200 dark:bg-slate-800" />
                  <CardContent className="p-4 space-y-3">
                    <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded w-3/4" />
                    <div className="h-4 bg-slate-200 dark:bg-slate-800 rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div 
              ref={recentCarouselRef}
              onScroll={checkRecentScrollButtons}
              className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide pb-4 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {recentCases.slice(0, 12).map((person, index) => (
                <div key={person.id} className="flex-shrink-0 w-[280px] md:w-[320px]">
                  <MissingPersonCard person={person} index={index} />
                </div>
              ))}
            </div>
          )}

          {/* Mobile Browse All link */}
          <div className="mt-6 text-center sm:hidden">
            <Link to="/browse">
              <Button variant="outline" className="text-trust-blue-500 border-trust-blue-300 hover:bg-trust-blue-50">
                Browse All Cases <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Recent Findings */}
      <section className="py-10 md:py-16 lg:py-24 bg-slate-950" data-testid="recent-findings-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-14">
            <Badge variant="outline" className="mb-3 border-emerald-500/40 text-emerald-400 bg-emerald-500/10 text-xs tracking-wide">
              Active Investigations
            </Badge>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white">
              Recent Findings
            </h2>
            <p className="mt-3 text-slate-400 max-w-2xl mx-auto text-sm md:text-base">
              Cases where our algorithm surfaced leads that traditional searches missed.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {/* Finding 1 — Brooks County */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="bg-slate-900/80 border-slate-800 h-full hover:border-emerald-500/30 transition-colors" data-testid="finding-brooks-county">
                <CardContent className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-amber-400" />
                    </div>
                    <Badge className="bg-amber-500/15 text-amber-400 border-0 text-[10px] font-semibold tracking-wider uppercase">
                      Cross-Border
                    </Badge>
                  </div>
                  <h3 className="font-heading font-bold text-white text-base md:text-lg mb-1">
                    Brooks County, TX
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">Cross-Border Match</p>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    Everardo Rivera Buenrostro, 37, last seen Tamaulipas Mexico, matched against an unidentified male recovered in Brooks County TX 29 days later. Exact age, compatible clothing, same migration corridor.
                  </p>
                  <div className="mt-4 pt-3 border-t border-slate-800">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-400">
                      <Dna className="w-3 h-3" />
                      DNA comparison pending
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Finding 2 — Palm Bay */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card className="bg-slate-900/80 border-slate-800 h-full hover:border-emerald-500/30 transition-colors" data-testid="finding-palm-bay">
                <CardContent className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-red-500/15 flex items-center justify-center flex-shrink-0">
                      <FileSearch className="w-4 h-4 text-red-400" />
                    </div>
                    <Badge className="bg-red-500/15 text-red-400 border-0 text-[10px] font-semibold tracking-wider uppercase">
                      Cold Case
                    </Badge>
                  </div>
                  <h3 className="font-heading font-bold text-white text-base md:text-lg mb-1">
                    Palm Bay, FL
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">Crutchley Era</p>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    An unidentified woman found wrapped in a blanket 8 miles from convicted kidnapper John Crutchley's home in 1985 has never been identified. Three missing women from the same era now have scored matches.
                  </p>
                  <div className="mt-4 pt-3 border-t border-slate-800">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-400">
                      <Dna className="w-3 h-3" />
                      DNA comparison recommended
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Finding 3 — Passaic River */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card className="bg-slate-900/80 border-slate-800 h-full hover:border-emerald-500/30 transition-colors" data-testid="finding-passaic-river">
                <CardContent className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-4 h-4 text-violet-400" />
                    </div>
                    <Badge className="bg-violet-500/15 text-violet-400 border-0 text-[10px] font-semibold tracking-wider uppercase">
                      Behavioral
                    </Badge>
                  </div>
                  <h3 className="font-heading font-bold text-white text-base md:text-lg mb-1">
                    Passaic River, NJ
                  </h3>
                  <p className="text-xs text-slate-500 mb-3">Behavioral Convergence</p>
                  <p className="text-sm text-slate-300 leading-relaxed">
                    A missing woman who left a suicide note in January 1991 matches a Passaic River Jane Doe found 17 months later. The Jane Doe had old suicidal cuts on both wrists and a mid-line abdominal scar consistent with the MP's record. Weight difference consistent with late-stage Huntington's disease progression.
                  </p>
                  <div className="mt-4 pt-3 border-t border-slate-800">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-400">
                      <Dna className="w-3 h-3" />
                      High priority — scar + behavioral match
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="text-center mt-8">
            <Link to="/matches">
              <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="view-all-findings-btn">
                View All Matches
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Alert Subscription */}
      <section className="py-12 md:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile: Clean, focused design */}
          <Card className="md:hidden bg-slate-900 border-0 overflow-hidden">
            <CardContent className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-trust-blue-500/20 mb-4">
                <Bell className="w-6 h-6 text-trust-blue-400" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">
                Stay in the Loop
              </h2>
              <p className="text-slate-300 text-sm mb-5">
                Get notified when cases are reported near you.
              </p>
              
              <form onSubmit={handleSubscribe} className="space-y-3">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400 rounded-xl text-center"
                  data-testid="subscribe-email-mobile"
                />
                <Button 
                  type="submit"
                  className="w-full h-12 bg-trust-blue-500 hover:bg-trust-blue-600 text-white rounded-xl font-semibold"
                  data-testid="subscribe-btn-mobile"
                >
                  Keep Me Updated
                </Button>
              </form>
              
              <p className="text-xs text-slate-500 mt-3">
                We respect your inbox. Unsubscribe anytime.
              </p>
            </CardContent>
          </Card>
          
          {/* Desktop: Original two-column layout */}
          <Card className="hidden md:block bg-gradient-to-r from-safety-orange-500 to-trust-blue-500 border-0 overflow-hidden">
            <CardContent className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Bell className="w-6 h-6 text-white" />
                    <span className="text-white/80 font-medium">Stay Alert</span>
                  </div>
                  <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-4">
                    Get Notified About Cases in Your Area
                  </h2>
                  <p className="text-white/80 mb-2">
                    Subscribe to receive alerts when new missing person cases are reported in your area.
                  </p>
                  <p className="text-white/60 text-sm mb-0 lg:mb-0">
                    We'll only notify you about relevant cases. No spam.
                  </p>
                </div>

                <form onSubmit={handleSubscribe} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-14 bg-white/10 border-white/20 text-white placeholder:text-white/60 rounded-xl focus:bg-white/20"
                    data-testid="subscribe-email"
                  />
                  <Input
                    type="text"
                    placeholder="Your city or region"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="h-14 bg-white/10 border-white/20 text-white placeholder:text-white/60 rounded-xl focus:bg-white/20"
                    data-testid="subscribe-location"
                  />
                  <Button 
                    type="submit"
                    className="w-full h-14 bg-white text-safety-orange-600 hover:bg-white/90 rounded-xl font-bold text-lg"
                    data-testid="subscribe-btn"
                  >
                    Subscribe to Alerts
                  </Button>
                </form>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
    </>
  );
}
